// Cognito sign-in for both audiences.
//
// Two pools, so the login form has to know which one it is talking to before it
// can talk to it. That is a deliberate consequence of MFA being a pool-wide
// setting: staff must have it, and SFTP password users cannot (Transfer Family
// has no way to answer a challenge), so they cannot share a pool. See
// infrastructure/lib/sftp-identity.ts.
//
// Everything here uses SRP, so the password itself never crosses the network --
// only a proof of knowing it. That is also why the app client has no secret: a
// secret shipped inside a downloadable bundle is not a secret.
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { loadConfig } from './config';
import { federatedIdentity, isFederated, ssoSignOut } from './sso';

export type Audience = 'admin' | 'partner';

export interface Session {
  audience: Audience;
  username: string;
  email?: string;
  idToken: string;
}

// The states Cognito can leave a sign-in in. Each one carries the continuation
// that finishes it, so the UI never has to hold a CognitoUser itself.
export type SignInOutcome =
  | { kind: 'signed-in'; session: Session }
  | { kind: 'new-password'; submit: (password: string) => Promise<SignInOutcome> }
  | { kind: 'totp'; submit: (code: string) => Promise<SignInOutcome> }
  | {
      kind: 'totp-setup';
      secret: string;
      // Paste into an authenticator app that supports it, for the users who
      // would rather not type a 32-character secret by hand.
      otpauthUri: string;
      submit: (code: string) => Promise<SignInOutcome>;
    };

// Which pool the stored tokens belong to. The SDK keys its localStorage entries
// by client id, so without this marker there is no way to tell a leftover admin
// session from a partner one.
const AUDIENCE_KEY = 'trx-sftp-portal.audience';

const poolFor = async (audience: Audience): Promise<CognitoUserPool> => {
  const config = await loadConfig();
  return new CognitoUserPool(
    audience === 'admin'
      ? { UserPoolId: config.adminPoolId, ClientId: config.adminClientId }
      : { UserPoolId: config.partnerPoolId, ClientId: config.partnerClientId }
  );
};

export const signIn = async (
  audience: Audience,
  username: string,
  password: string
): Promise<SignInOutcome> => {
  const pool = await poolFor(audience);
  const user = new CognitoUser({ Username: username, Pool: pool });

  return new Promise<SignInOutcome>((resolve, reject) => {
    user.authenticateUser(
      new AuthenticationDetails({ Username: username, Password: password }),
      {
        onSuccess: (session) => resolve(finish(audience, user, session)),
        onFailure: reject,

        // An admin-created account starts here. Cognito will not issue tokens
        // until the temporary password is replaced.
        newPasswordRequired: () =>
          resolve({
            kind: 'new-password',
            submit: (newPassword) =>
              new Promise<SignInOutcome>((ok, fail) => {
                // The second argument is for required attributes Cognito wants
                // filled in; email is already set by whoever created the user.
                user.completeNewPasswordChallenge(newPassword, {}, {
                  onSuccess: (session) => ok(finish(audience, user, session)),
                  onFailure: fail,
                  // Replacing the password can lead straight into MFA setup.
                  mfaSetup: () => ok(beginTotpSetup(audience, user)),
                  totpRequired: () => ok(awaitTotp(audience, user)),
                });
              }),
          }),

        // MFA is required on this pool but this user has no authenticator yet.
        mfaSetup: () => resolve(beginTotpSetup(audience, user)),
        totpRequired: () => resolve(awaitTotp(audience, user)),
      }
    );
  });
};

const awaitTotp = (audience: Audience, user: CognitoUser): SignInOutcome => ({
  kind: 'totp',
  submit: (code) =>
    new Promise<SignInOutcome>((resolve, reject) => {
      user.sendMFACode(
        code,
        {
          onSuccess: (session) => resolve(finish(audience, user, session)),
          onFailure: reject,
        },
        'SOFTWARE_TOKEN_MFA'
      );
    }),
});

// Asynchronous because the secret has to be fetched from Cognito before the UI
// has anything to display. Callers hand the promise straight to `resolve`, which
// unwraps it.
const beginTotpSetup = (audience: Audience, user: CognitoUser): Promise<SignInOutcome> =>
  new Promise<SignInOutcome>((resolveOutcome, rejectOutcome) => {
    user.associateSoftwareToken({
      associateSecretCode: (secret) => {
        const label = encodeURIComponent(`${ISSUER}:${user.getUsername()}`);
        resolveOutcome({
          kind: 'totp-setup',
          secret,
          otpauthUri: `otpauth://totp/${label}?secret=${secret}&issuer=${encodeURIComponent(ISSUER)}`,
          submit: (code) =>
            new Promise<SignInOutcome>((resolve, reject) => {
              user.verifySoftwareToken(code, 'portal', {
                onSuccess: (session) => {
                  // Registering the authenticator does not by itself make it the
                  // factor Cognito asks for next time. Without this, the next
                  // sign-in loops back into setup.
                  user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (err) =>
                    err ? reject(err) : resolve(finish(audience, user, session))
                  );
                },
                onFailure: reject,
              });
            }),
        });
      },
      onFailure: rejectOutcome,
    });
  });

// Shown in the authenticator app's list, so it is worth being recognisable.
const ISSUER = 'TRX SFTP portal';

const finish = (
  audience: Audience,
  user: CognitoUser,
  session: CognitoUserSession
): SignInOutcome => {
  localStorage.setItem(AUDIENCE_KEY, audience);
  return { kind: 'signed-in', session: sessionFrom(audience, user, session) };
};

const sessionFrom = (
  audience: Audience,
  user: CognitoUser,
  session: CognitoUserSession
): Session => {
  const claims = session.getIdToken().payload as Record<string, string>;
  return {
    audience,
    username: claims['cognito:username'] ?? user.getUsername(),
    email: claims.email,
    idToken: session.getIdToken().getJwtToken(),
  };
};

// Returns the signed-in session, refreshing the tokens if they have expired.
// Called before every API request rather than once at startup, because a 30
// minute access token will expire while a page is open.
export const currentSession = async (): Promise<Session | undefined> => {
  // Federated first, and not keyed on AUDIENCE_KEY: those tokens came from the
  // hosted UI rather than the SDK, so there is no CognitoUser in its storage for
  // the pool lookup below to find. Staff SSO is admin-only by construction --
  // see sftp-config.ts for why partners cannot federate.
  const federated = await federatedIdentity();
  if (federated) return { audience: 'admin', ...federated };

  const audience = localStorage.getItem(AUDIENCE_KEY) as Audience | null;
  if (audience !== 'admin' && audience !== 'partner') return undefined;

  const pool = await poolFor(audience);
  const user = pool.getCurrentUser();
  if (!user) return undefined;

  return new Promise<Session | undefined>((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      // Any failure here means "not signed in": an expired refresh token, a
      // revoked session, a deleted user. All of them lead to the login screen.
      if (err || !session?.isValid()) {
        resolve(undefined);
        return;
      }
      resolve(sessionFrom(audience, user, session));
    });
  });
};

export const signOut = async (): Promise<void> => {
  const audience = localStorage.getItem(AUDIENCE_KEY) as Audience | null;
  localStorage.removeItem(AUDIENCE_KEY);

  // A federated session also has to end at the identity provider, so this
  // navigates away and never returns to the caller.
  if (isFederated()) {
    await ssoSignOut();
    return;
  }

  if (audience !== 'admin' && audience !== 'partner') return;

  const pool = await poolFor(audience);
  const user = pool.getCurrentUser();
  // globalSignOut would also revoke the refresh token server-side, but it fails
  // if the session has already expired -- which is exactly when people click
  // "sign out". signOut clears the local tokens unconditionally.
  user?.signOut();
};

export const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
  const audience = localStorage.getItem(AUDIENCE_KEY) as Audience | null;
  if (!audience) throw new Error('Not signed in');
  const pool = await poolFor(audience);
  const user = pool.getCurrentUser();
  if (!user) throw new Error('Not signed in');

  await new Promise<void>((resolve, reject) => {
    // changePassword needs a current session on the user object, which
    // getCurrentUser alone does not attach.
    user.getSession((err: Error | null) => {
      if (err) {
        reject(err);
        return;
      }
      user.changePassword(oldPassword, newPassword, (changeErr) =>
        changeErr ? reject(changeErr) : resolve()
      );
    });
  });
};

// Self-service reset, which is why both pools require a verified email address.
export const requestPasswordReset = async (
  audience: Audience,
  username: string
): Promise<void> => {
  const pool = await poolFor(audience);
  const user = new CognitoUser({ Username: username, Pool: pool });
  await new Promise<void>((resolve, reject) => {
    user.forgotPassword({ onSuccess: () => resolve(), onFailure: reject });
  });
};

export const confirmPasswordReset = async (
  audience: Audience,
  username: string,
  code: string,
  newPassword: string
): Promise<void> => {
  const pool = await poolFor(audience);
  const user = new CognitoUser({ Username: username, Pool: pool });
  await new Promise<void>((resolve, reject) => {
    user.confirmPassword(code, newPassword, { onSuccess: () => resolve(), onFailure: reject });
  });
};
